import { BaseComponent } from './BaseComponent';
import { Announcement, PaginationMetadata, AnnouncementsResponse } from '../types';
import { Config } from '../core/Config';
import { sanitizeHtml, sanitizeUrl } from '../utils/sanitize';

export class AnnouncementList extends BaseComponent {
    // The list opens instantly in a loading state while the orchestrator
    // fetches the first page in the background; these three are public so it
    // can write the results back and re-render via updateContent().
    public announcements: Announcement[];
    public pagination: PaginationMetadata | undefined;
    public isLoading: boolean = false;
    // When set, the body renders a translated error message instead of the
    // list/empty/loading states — used when the fetch that should populate the
    // (already-visible) modal fails, so the user sees a clear failure with the
    // close affordances intact rather than an endless spinner or a misleading
    // "no announcements" empty state.
    public hasError: boolean = false;

    private onClose: () => void;
    private onAnnouncementClick: (announcement: Announcement) => void;
    private fetchPage: (page: number, perPage: number) => Promise<AnnouncementsResponse>;
    private showBranding: boolean;

    constructor(
        config: Config,
        announcements: Announcement[],
        pagination: PaginationMetadata | undefined,
        onClose: () => void,
        onAnnouncementClick: (announcement: Announcement) => void,
        fetchPage: (page: number, perPage: number) => Promise<AnnouncementsResponse>,
        showBranding: boolean = true
    ) {
        super(config);
        this.announcements = announcements;
        this.pagination = pagination;
        this.onClose = onClose;
        this.onAnnouncementClick = onAnnouncementClick;
        this.fetchPage = fetchPage;
        this.showBranding = showBranding;
    }

    protected render(): HTMLElement {
        const overlay = this.createElement('div', 'pr-overlay pr-sdk');
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-labelledby', 'pr-list-title');

        const modal = this.createElement('div', 'pr-list-modal');

        // Header
        const header = this.createElement('div', 'pr-list-header');

        const title = this.createElement('h2', 'pr-list-title');
        title.id = 'pr-list-title';
        title.textContent = this.config.t.whatsNew;
        header.appendChild(title);

        const closeBtn = this.createElement('button', 'pr-list-close');
        closeBtn.innerHTML = '&times;';
        closeBtn.setAttribute('aria-label', this.config.t.close);
        closeBtn.onclick = () => this.handleClose();
        header.appendChild(closeBtn);

        modal.appendChild(header);

        // Content
        const content = this.createElement('div', 'pr-list-content');
        this.appendContentBody(content);
        modal.appendChild(content);

        // Pagination controls (only show if there's pagination metadata with more than one page)
        if (this.pagination && this.pagination.last_page > 1) {
            const pagination = this.renderPaginationControls();
            modal.appendChild(pagination);
        }

        // Footer with the close button and optional branding.
        const footer = this.createElement('div', 'pr-list-footer');

        const closeButton = this.createElement('button', 'pr-list-close-btn');
        closeButton.textContent = this.config.t.close;
        closeButton.onclick = () => this.handleClose();
        footer.appendChild(closeButton);

        if (this.showBranding) {
            const poweredBy = this.createElement('a', 'pr-powered-by');
            poweredBy.textContent = 'Powered by PulseRelease.com';
            poweredBy.href = 'https://pulserelease.com';
            poweredBy.target = '_blank';
            poweredBy.rel = 'noopener noreferrer';
            footer.appendChild(poweredBy);
        }

        modal.appendChild(footer);

        overlay.appendChild(modal);

        // Close on overlay click
        overlay.onclick = (e) => {
            if (e.target === overlay) {
                this.handleClose();
            }
        };

        return overlay;
    }

    protected lockScroll(): boolean {
        return true;
    }

    protected closeOnEscape(): boolean {
        return true;
    }

    protected onEscape(): void {
        this.handleClose();
    }

    /**
     * Fill the list body with the state-appropriate content — loading spinner,
     * error message, empty notice, or the announcement items. Shared by the
     * initial render() and the in-place updateContent() so both stay in sync.
     */
    private appendContentBody(contentElement: HTMLElement): void {
        if (this.isLoading) {
            const loading = this.createElement('div', 'pr-list-loading');
            loading.textContent = this.config.t.loading;
            contentElement.appendChild(loading);
        } else if (this.hasError) {
            const error = this.createElement('div', 'pr-list-error');
            error.setAttribute('role', 'alert');
            error.textContent = this.config.t.listError;
            contentElement.appendChild(error);
        } else if (this.announcements.length === 0) {
            const empty = this.createElement('div', 'pr-list-empty');
            empty.textContent = this.config.t.noAnnouncements;
            contentElement.appendChild(empty);
        } else {
            this.announcements.forEach((announcement) => {
                const item = this.renderAnnouncementItem(announcement);
                contentElement.appendChild(item);
            });
        }
    }

    private renderAnnouncementItem(announcement: Announcement): HTMLElement {
        const item = this.createElement('div', 'pr-announcement-item');
        // The whole row is clickable AND keyboard-operable to open the
        // announcement's full view.
        item.setAttribute('role', 'button');
        item.setAttribute('tabindex', '0');
        item.style.cursor = 'pointer';
        item.onclick = () => this.onAnnouncementClick(announcement);
        item.onkeydown = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.onAnnouncementClick(announcement);
            }
        };

        const header = this.createElement('div', 'pr-announcement-header');

        const titleWrapper = this.createElement('div', 'pr-announcement-title-wrapper');

        const title = this.createElement('h3', 'pr-announcement-item-title');
        title.textContent = announcement.title;
        titleWrapper.appendChild(title);

        const date = this.createElement('div', 'pr-announcement-date');
        date.textContent = this.formatDate(announcement.publish_at);
        titleWrapper.appendChild(date);

        header.appendChild(titleWrapper);

        const badge = this.createElement('span', `pr-announcement-badge ${announcement.type}`);
        badge.textContent = this.formatType(announcement.type);
        header.appendChild(badge);

        item.appendChild(header);

        // Image (if exists) - positioned between title and description
        if (announcement.image_url) {
            // Guard the image URL scheme just like the CTA link below.
            const safeSrc = sanitizeUrl(announcement.image_url);
            if (safeSrc) {
                const img = this.createElement('img', 'pr-announcement-image');
                img.src = safeSrc;
                img.alt = announcement.title;
                item.appendChild(img);
            }
        }

        // Description
        const description = this.createElement('div', 'pr-announcement-description');
        description.innerHTML = sanitizeHtml(announcement.description);
        item.appendChild(description);

        // Action button (if exists)
        if (announcement.action_button_text && announcement.action_button_url) {
            const ctaBtn = this.createElement('a', 'pr-announcement-cta');
            ctaBtn.textContent = announcement.action_button_text;
            // Drop the href entirely if it uses a dangerous scheme, rather than
            // rendering a clickable javascript:/data: link.
            ctaBtn.href = sanitizeUrl(announcement.action_button_url) || '#';
            ctaBtn.target = '_blank';
            ctaBtn.rel = 'noopener noreferrer';
            // The CTA navigates to its own URL; stop propagation so it doesn't
            // also trigger the row's open-announcement handler.
            ctaBtn.onclick = (e) => {
                e.stopPropagation();
            };

            // Apply custom button color if specified
            if (announcement.action_button_color) {
                ctaBtn.style.backgroundColor = announcement.action_button_color;
                ctaBtn.style.borderColor = announcement.action_button_color;
            }

            item.appendChild(ctaBtn);
        }

        return item;
    }

    private renderPaginationControls(): HTMLElement {
        const paginationContainer = this.createElement('div', 'pr-list-pagination');

        if (!this.pagination) {
            return paginationContainer;
        }

        const prevBtn = this.createElement('button', 'pr-pagination-btn');
        prevBtn.textContent = `‹ ${this.config.t.previous}`;
        prevBtn.disabled = this.pagination.current_page === 1 || this.isLoading;
        prevBtn.onclick = () => this.goToPage(this.pagination!.current_page - 1);
        paginationContainer.appendChild(prevBtn);

        const pageInfo = this.createElement('span', 'pr-pagination-info');
        // Prefer a full localized template when the language provides one (some
        // languages, e.g. Chinese, can't be composed as `page N of M` around
        // bare words); otherwise fall back to the generic composition so every
        // other locale renders exactly as before.
        pageInfo.textContent = this.config.t.pageOf
            ? this.config.t.pageOf
                  .replace('{current}', String(this.pagination.current_page))
                  .replace('{last}', String(this.pagination.last_page))
            : `${this.config.t.page} ${this.pagination.current_page} ${this.config.t.of} ${this.pagination.last_page}`;
        paginationContainer.appendChild(pageInfo);

        const nextBtn = this.createElement('button', 'pr-pagination-btn');
        nextBtn.textContent = `${this.config.t.next} ›`;
        nextBtn.disabled =
            this.pagination.current_page >= this.pagination.last_page || this.isLoading;
        nextBtn.onclick = () => this.goToPage(this.pagination!.current_page + 1);
        paginationContainer.appendChild(nextBtn);

        return paginationContainer;
    }

    private async goToPage(page: number): Promise<void> {
        if (!this.pagination || page < 1 || page > this.pagination.last_page || this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.updateContent();

        try {
            // Fetch new page from server
            const response = await this.fetchPage(page, this.pagination.per_page);

            // Update state
            this.announcements = response.announcements;
            this.pagination = response.pagination;
            this.isLoading = false;

            // Update content with new data
            this.updateContent();
        } catch (error) {
            this.config.error('Failed to fetch page:', error);
            this.isLoading = false;
            this.updateContent();
        }
    }

    /**
     * Re-render the list body and pagination controls in place from the
     * current `announcements` / `pagination` / `isLoading` values. No-ops
     * while the modal isn't mounted.
     */
    public updateContent(): void {
        if (!this.container) {
            return;
        }

        // Find the content and pagination elements
        const contentElement = this.container.querySelector('.pr-list-content');
        const existingPagination = this.container.querySelector('.pr-list-pagination');
        const modal = this.container.querySelector('.pr-list-modal');

        if (!contentElement || !modal) {
            return;
        }

        // Clear and update content
        contentElement.innerHTML = '';
        this.appendContentBody(contentElement as HTMLElement);

        // Update pagination controls
        if (existingPagination) {
            const newPagination = this.renderPaginationControls();
            existingPagination.replaceWith(newPagination);
        } else if (this.pagination && this.pagination.last_page > 1) {
            // Add pagination if it didn't exist before
            const pagination = this.renderPaginationControls();
            // Insert before footer if exists, otherwise append to modal
            const footer = modal.querySelector('.pr-list-footer');
            if (footer) {
                modal.insertBefore(pagination, footer);
            } else {
                modal.appendChild(pagination);
            }
        }
    }

    private formatType(type: string): string {
        // `type` comes from the server, so tolerate values newer than this
        // build's translation table by falling back to a de-snake-cased label.
        const labels: Record<string, string> = this.config.t.announcementTypes;
        return labels[type] || type.replace(/_/g, ' ');
    }

    private handleClose(): void {
        this.hide();
        this.onClose();
    }
}
